<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <section class="content-section">
        <div class="container narrow">

          <!-- Loading -->
          <div v-if="loading" class="state-card">
            <div class="spinner"></div>
            <h3>Loading your demand notice…</h3>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="state-card error">
            <h3>Notice not available</h3>
            <p>{{ error }}</p>
            <a href="#/" class="btn-secondary">Back to home</a>
          </div>

          <!-- Notice -->
          <div v-else-if="property" class="notice-card">

            <!-- Letterhead -->
            <div class="notice-header">
              <div class="county-name">County Government of {{ county.countyName || '' }}</div>
              <h1 class="notice-title">Demand Notice</h1>
              <p class="notice-act">The Rating Act (CAP 267)</p>
              <p class="notice-date">Notice Date: {{ today }}</p>
            </div>

            <!-- Salutation -->
            <p class="salutation">Dear <strong>{{ property.ownerNames || 'Property Owner' }}</strong>,</p>

            <p class="intro">
              This is an official demand notice for outstanding land rates and ground rent owed
              on the property below. Please settle the balance to avoid further penalties and
              interest under the Rating Act.
            </p>

            <!-- Property summary -->
            <div class="info-block">
              <div class="info-row"><span>LR / Plot No.</span><strong>{{ property.LRNumber || '—' }}</strong></div>
              <div class="info-row" v-if="property.UPN"><span>UPN</span><strong>{{ property.UPN }}</strong></div>
              <div class="info-row" v-if="property.subCountyName"><span>Sub-County</span><strong>{{ property.subCountyName }}</strong></div>
              <div class="info-row" v-if="property.wardName"><span>Ward</span><strong>{{ property.wardName }}</strong></div>
              <div class="info-row" v-if="property.percelAddress"><span>Address</span><strong>{{ property.percelAddress }}</strong></div>
            </div>

            <!-- Financial breakdown -->
            <h3 class="section-title">Outstanding amount</h3>
            <table class="breakdown">
              <tbody>
                <tr v-if="num(property.annualLandRates)"><td>Annual Land Rates</td><td class="r">KES {{ money(property.annualLandRates) }}</td></tr>
                <tr v-if="num(property.annualGroundRent)"><td>Annual Ground Rent</td><td class="r">KES {{ money(property.annualGroundRent) }}</td></tr>
                <tr v-if="num(property.landRatesArrears)"><td>Land Rates Arrears</td><td class="r">KES {{ money(property.landRatesArrears) }}</td></tr>
                <tr v-if="num(property.groundRentArrears)"><td>Ground Rent Arrears</td><td class="r">KES {{ money(property.groundRentArrears) }}</td></tr>
                <tr v-if="num(property.accumPenalty)"><td>Accumulated Penalty</td><td class="r">KES {{ money(property.accumPenalty) }}</td></tr>
                <tr v-if="num(property.accumInterest)"><td>Accumulated Interest</td><td class="r">KES {{ money(property.accumInterest) }}</td></tr>
                <tr v-if="num(property.payments)" class="payments"><td>Less: Payments Received</td><td class="r">- KES {{ money(property.payments) }}</td></tr>
              </tbody>
              <tfoot>
                <tr class="total"><td>Amount Due</td><td class="r">KES {{ money(currentBalance) }}</td></tr>
              </tfoot>
            </table>

            <!-- CTA -->
            <div class="cta-row">
              <button class="btn-primary" @click="openPay" :disabled="!currentBalance || currentBalance <= 0">
                Pay Now
              </button>
              <button class="btn-secondary" @click="printNotice">
                Print Notice
              </button>
            </div>

            <!-- Footer note -->
            <p class="footnote">
              Payments can be made online via this portal, at county offices, or through any
              approved electronic means. Reference your LR number when paying.
            </p>
          </div>

        </div>
      </section>
    </main>

    <FooterComponent />

    <!-- ======================== PAYMENT MODAL ======================== -->
    <div v-if="payModal.open" class="pay-overlay" @click.self="closePay">
      <div class="pay-modal">

        <!-- Step 1: amount -->
        <div v-if="payModal.step === 'amount'">
          <h3 class="pay-title">How much would you like to pay?</h3>
          <p class="pay-sub">You can pay the full amount due, or a partial amount.</p>
          <label class="pay-label">Amount (KES)</label>
          <input type="number" class="pay-input" v-model.number="payModal.amount" min="1" step="0.01" autofocus>
          <div class="pay-quickrow">
            <button class="chip" @click="payModal.amount = currentBalance">Full balance · KES {{ money(currentBalance) }}</button>
            <button v-if="num(property.annualCharges)" class="chip" @click="payModal.amount = parseFloat(property.annualCharges)">Annual · KES {{ money(property.annualCharges) }}</button>
          </div>
          <div v-if="payModal.error" class="pay-error">{{ payModal.error }}</div>
          <div class="pay-actions">
            <button class="btn-secondary" @click="closePay">Cancel</button>
            <button class="btn-primary" @click="confirmAmount">Continue</button>
          </div>
        </div>

        <!-- Step 2: phone -->
        <div v-else-if="payModal.step === 'phone'">
          <h3 class="pay-title">M-Pesa phone number</h3>
          <p class="pay-sub">You will receive an STK push on this number to complete payment of <strong>KES {{ money(payModal.amount) }}</strong>.</p>
          <label class="pay-label">Phone Number</label>
          <input type="tel" class="pay-input" v-model="payModal.phone" placeholder="e.g. 0712345678" autofocus>
          <div v-if="payModal.error" class="pay-error">{{ payModal.error }}</div>
          <div class="pay-actions">
            <button class="btn-secondary" @click="payModal.step = 'amount'">Back</button>
            <button class="btn-primary" @click="startPayment" :disabled="payModal.processing">
              {{ payModal.processing ? 'Generating bill…' : 'Pay KES ' + money(payModal.amount) }}
            </button>
          </div>
        </div>

        <!-- Step 3: STK pending -->
        <div v-else-if="payModal.step === 'pending'" class="pay-state">
          <div class="spinner"></div>
          <h3>Check your phone</h3>
          <p>We've sent an M-Pesa STK push to <strong>{{ payModal.phone }}</strong> for <strong>KES {{ money(payModal.amount) }}</strong>.</p>
          <p class="muted small">Bill No. <code>{{ billGenerated.billNo }}</code> · Pay Bill <code>{{ billGenerated.payBillNo }}</code></p>
          <p class="muted">Enter your M-Pesa PIN on your phone to complete the payment. This page will update automatically.</p>
          <button class="btn-secondary" @click="closePay">Close</button>
        </div>

        <!-- Step 4: success -->
        <div v-else-if="payModal.step === 'done'" class="pay-state success">
          <div class="check">✓</div>
          <h3>Payment received</h3>
          <p>Thank you. Your payment of <strong>KES {{ money(payModal.amount) }}</strong> has been received.</p>
          <p class="muted small" v-if="receiptNo">Receipt No. <code>{{ receiptNo }}</code></p>
          <div class="pay-actions">
            <button v-if="receiptNo" class="btn-primary" @click="downloadReceipt">
              <i class="bx bx-download me-1"></i> Download Receipt
            </button>
            <button class="btn-secondary" @click="closePay">Done</button>
          </div>
        </div>

        <!-- Step 5: failure -->
        <div v-else-if="payModal.step === 'failed'" class="pay-state error">
          <h3>Payment not completed</h3>
          <p>{{ payModal.error || 'Something went wrong. Please try again.' }}</p>
          <div class="pay-actions">
            <button class="btn-secondary" @click="closePay">Close</button>
            <button class="btn-primary" @click="payModal.step = 'phone'; payModal.error = null">Try again</button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, execute2, biller, paysol, landrate } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PublicDemandNotice",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      loading: true,
      error: null,
      property: null,
      county: {},

      // Fee lookup (mirrors the staff Bill Client flow + portal /land-rate)
      feesAndCharge: null,
      feeID: null,

      payModal: {
        open: false,
        step: 'amount',     // amount | phone | pending | done | failed
        amount: 0,
        phone: '',
        processing: false,
        error: null,
        pollHandle: null
      },

      billGenerated: { billNo: null, payBillNo: null, amount: null },
      receiptNo: null
    };
  },
  computed: {
    propertyId() { return this.$route.query.id || ''; },
    linkToken()  { return this.$route.query.t  || ''; },
    currentBalance() {
      const p = this.property || {};
      return parseFloat(p.currentBalance || p.outstandingArrears || 0) || 0;
    },
    today() {
      return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  },
  mounted() {
    this.load();
    this.getIncomeTypes();
  },
  beforeUnmount() {
    if (this.payModal.pollHandle) clearInterval(this.payModal.pollHandle);
  },
  methods: {
    num(v) { return parseFloat(v || 0) > 0; },
    money(v) {
      const n = parseFloat(v || 0) || 0;
      return n.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    load() {
      if (!this.propertyId) {
        this.loading = false;
        this.error = "This link is missing a property reference.";
        return;
      }
      if (!this.linkToken) {
        this.loading = false;
        this.error = "This link is missing its security token. Please open the most recent SMS or email we sent you, or use the portal to look up your property.";
        return;
      }
      const data = new FormData();
      data.append('function', 'getPropertyForDemandNotice');
      data.append('propertyId', this.propertyId);
      data.append('linkToken', this.linkToken);
      execute(data, landrate)
          .then((res) => {
            this.loading = false;
            if (res.data && res.data.success) {
              this.property = res.data.data.property;
              this.county   = res.data.data.county || {};
            } else {
              this.error = (res.data && res.data.message) || "We couldn't find this property.";
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "Network error — please try again.";
          });
    },

    // Look up the LANDRATE feeId so generateBill3 has the same fee the
    // staff Bill Client uses. Mirrors the existing portal /land-rate flow.
    getIncomeTypes() {
      const data = new FormData();
      data.append('function', 'getIncomeTypes');
      data.append('incomeTypePrefix', 'LANDRATE');
      data.append('keyword', 'Land Rate');
      execute(data, biller).then((res) => {
        if (res.data && res.data.success) {
          const types = res.data.data.incomeTypes || [];
          if (types.length) this.getFeesAndChargesBilling(types[0].incomeTypeId);
        }
      }).catch((e) => console.error(e));
    },

    getFeesAndChargesBilling(incomeTypeId) {
      const data = new FormData();
      data.append('function', 'getFeesAndCharges');
      data.append('incomeTypeId', incomeTypeId);
      execute(data, biller).then((res) => {
        if (res.data && res.data.success) {
          const fees = res.data.data.feesAndCharges || [];
          if (fees.length) {
            this.feesAndCharge = fees[0];
            this.feeID = fees[0].feeId;
          }
        }
      }).catch((e) => console.error(e));
    },

    openPay() {
      this.payModal.open = true;
      this.payModal.step = 'amount';
      this.payModal.amount = this.currentBalance;
      this.payModal.phone = (this.property && this.property.ownerPhoneNo) || '';
      this.payModal.error = null;
    },

    closePay() {
      this.payModal.open = false;
      this.payModal.error = null;
      this.payModal.processing = false;
      if (this.payModal.pollHandle) {
        clearInterval(this.payModal.pollHandle);
        this.payModal.pollHandle = null;
      }
    },

    confirmAmount() {
      const a = parseFloat(this.payModal.amount);
      if (!a || a <= 0) {
        this.payModal.error = 'Please enter an amount greater than zero.';
        return;
      }
      this.payModal.error = null;
      this.payModal.step = 'phone';
    },

    startPayment() {
      const phone = (this.payModal.phone || '').trim();
      if (!phone) {
        this.payModal.error = 'Please enter your M-Pesa phone number.';
        return;
      }
      if (!this.feeID) {
        this.payModal.error = 'Land rate fee not loaded yet. Please wait a moment and try again.';
        return;
      }
      this.payModal.error = null;
      this.payModal.processing = true;
      this.generateBill();
    },

    // Build the EXACT payload shape the staff Bill Client uses, so the bill
    // appears the same way in receipting / reporting as bills raised at the
    // counter. Reference payload (one item):
    //   { unitFeeAmount, revenueStreamItem, feeId, amount, customer (= property.id),
    //     zone, subCountyID, subCountyName, wardID, wardName,
    //     idNo, phoneNumber, names, customerPhoneNumber,
    //     description (rich), customerName }
    buildBillItem() {
      const p = this.property || {};
      const fc = this.feesAndCharge || {};
      const amount = parseFloat(this.payModal.amount) || 0;

      const description =
          `Property ID: ${p.id || ''} | UPN NO.: ${p.UPN || ''} | Block No.: ${p.block || ''} | ` +
          `Plot No.: ${p.LRNumber || ''} | Address: ${p.percelAddress || ''} | Name: ${p.ownerNames || ''} | ` +
          `CHARGES - Annual Rate: KES ${this.money(p.annualCharges)}, ` +
          `Outstanding Arrears: KES ${this.money(p.outstandingArrears)}, ` +
          `Current Balance: KES ${this.money(p.currentBalance || p.outstandingArrears || 0)}`;

      const customerName = `${p.LRNumber || ''} | ${p.ownerNames || ''}`;

      return [{
        typeDescription: fc.typeDescription || '',
        feeDescription: fc.feeDescription || 'Land Rate Fee',
        unitFeeAmount: amount,
        accountNo: fc.accountNo || '',
        costCenterNo: fc.costCenterNo || '',
        incomeTypeId: fc.incomeTypeId || '',
        revenueStreamItem: String(p.id || ''),
        feeId: this.feeID,
        amount: amount,
        customer: String(p.id || ''),
        zone: p.zone || '',
        subCountyID: p.subCountyID || '',
        subCountyName: p.subCountyName || '',
        wardID: p.wardID || '',
        wardName: p.wardName || '',
        // No staff session — these stay blank for self-service. The customer
        // number below carries the citizen's M-Pesa phone.
        idNo: '',
        phoneNumber: '',
        names: p.ownerNames || '',
        customerPhoneNumber: this.payModal.phone,
        description: description,
        customerName: customerName
      }];
    },

    generateBill() {
      const billItem = this.buildBillItem();
      const data = new FormData();
      data.append('function', 'generateBill3');
      data.append('billItem', JSON.stringify(billItem));
      data.append('customerPhoneNumber', this.payModal.phone);
      data.append('messageCode', 'BLL');
      data.append('names', (this.property && this.property.ownerNames) || '');
      data.append('email', (this.property && this.property.ownerEmail) || '');

      execute(data, biller)
          .then((res) => {
            if (res.data && res.data.success) {
              this.billGenerated = res.data.data.billGenerated;
              this.payModal.step = 'pending';
              this.customerPayBillOnline();
            } else {
              this.payModal.processing = false;
              this.payModal.step = 'failed';
              this.payModal.error = (res.data && res.data.message) || 'Failed to generate bill.';
            }
          })
          .catch((e) => {
            this.payModal.processing = false;
            this.payModal.step = 'failed';
            this.payModal.error = e.message || 'Network error generating bill.';
          });
    },

    customerPayBillOnline() {
      const data = new FormData();
      data.append('function', 'customerPayBillOnline');
      data.append('payBillNumber', this.billGenerated.payBillNo);
      data.append('amount', this.billGenerated.amount);
      data.append('accountReference', this.billGenerated.billNo);
      data.append('transactionDesc', this.billGenerated.billNo);
      data.append('phoneNumber', this.payModal.phone);
      data.append('token', 'im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ');

      execute2(data, paysol)
          .then((res) => {
            if (res.data && res.data.success) {
              // Poll for payment confirmation (matches existing portal /land-rate)
              this.payModal.pollHandle = setInterval(() => {
                this.checkPayment().then((paid) => {
                  if (paid && this.payModal.pollHandle) {
                    clearInterval(this.payModal.pollHandle);
                    this.payModal.pollHandle = null;
                  }
                });
              }, 2000);
            } else {
              this.payModal.step = 'failed';
              this.payModal.error = (res.data && res.data.message) || 'Failed to send M-Pesa request.';
            }
          })
          .catch((e) => {
            this.payModal.step = 'failed';
            this.payModal.error = e.message || 'Network error sending M-Pesa request.';
          });
    },

    checkPayment() {
      const data = new FormData();
      data.append('function', 'checkPayment');
      data.append('accountRef', this.billGenerated.billNo);
      return execute(data, biller).then((res) => {
        if (res.data && res.data.success) {
          this.payModal.step = 'done';
          this.receiptNo = (res.data.data.receipt && res.data.data.receipt.receiptDetails && res.data.data.receipt.receiptDetails.receiptNo) || null;
          return true;
        }
        return false;
      }).catch(() => false);
    },

    printNotice() {
      window.print();
    },

    // Mirrors the existing portal /land-rate flow exactly: stash the
    // receiptNo in localStorage['params'] and open the named 'receipt'
    // route in a new tab, where the existing PrintReceipt component
    // renders the printable receipt.
    downloadReceipt() {
      if (!this.receiptNo) {
        alert('Receipt not available yet. Please wait a moment and try again.');
        return;
      }
      localStorage['params'] = JSON.stringify({ receiptNo: this.receiptNo });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
.page-wrapper { background: #f8fafc; min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; }
.content-section { padding: 40px 0 60px; }
.container.narrow { max-width: 780px; margin: 0 auto; padding: 0 20px; }

.state-card { background: #fff; border-radius: 16px; padding: 60px 30px; text-align: center; box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
.state-card.error h3 { color: #b91c1c; }
.spinner { width: 36px; height: 36px; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 16px; }
@keyframes spin { to { transform: rotate(360deg); } }

.notice-card { background: #fff; border-radius: 16px; padding: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); border-top: 6px solid #4ca82c; }
.notice-header { text-align: center; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; margin-bottom: 24px; }
.county-name { font-size: 14px; color: #475569; letter-spacing: 1px; text-transform: uppercase; font-weight: 600; }
.notice-title { font-size: 32px; color: #0045a1; margin: 8px 0; font-weight: 800; letter-spacing: 1px; }
.notice-act { font-style: italic; color: #6b7280; font-size: 14px; margin: 4px 0; }
.notice-date { font-size: 13px; color: #6b7280; margin-top: 6px; }

.salutation { font-size: 16px; margin: 0 0 12px; color: #111827; }
.intro { font-size: 14px; color: #374151; line-height: 1.6; margin-bottom: 24px; }

.info-block { background: #f9fafb; border-radius: 12px; padding: 16px 20px; margin-bottom: 24px; border: 1px solid #e5e7eb; }
.info-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; border-bottom: 1px dashed #e5e7eb; }
.info-row:last-child { border-bottom: 0; }
.info-row span { color: #6b7280; }
.info-row strong { color: #111827; }

.section-title { font-size: 16px; color: #111827; margin: 28px 0 12px; }
.breakdown { width: 100%; border-collapse: collapse; font-size: 14px; }
.breakdown td { padding: 10px 8px; border-bottom: 1px solid #f1f5f9; color: #374151; }
.breakdown td.r { text-align: right; font-variant-numeric: tabular-nums; }
.breakdown .payments td { color: #059669; }
.breakdown tfoot .total td { font-weight: 800; font-size: 16px; color: #b91c1c; padding-top: 14px; border-top: 2px solid #111827; border-bottom: 0; }

.cta-row { display: flex; gap: 12px; margin: 28px 0 8px; flex-wrap: wrap; }
.btn-primary, .btn-secondary { flex: 1; min-width: 180px; padding: 14px 24px; border-radius: 10px; font-weight: 700; font-size: 15px; cursor: pointer; border: 0; transition: transform 0.15s, box-shadow 0.15s; }
.btn-primary { background: linear-gradient(135deg, #4ca82c 0%, #2e8b1e 100%); color: #fff; }
.btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(76,168,44,0.3); }
.btn-primary:disabled { background: #9ca3af; cursor: not-allowed; transform: none; box-shadow: none; }
.btn-secondary { background: #fff; color: #1f2937; border: 1px solid #d1d5db; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; }
.btn-secondary:hover { background: #f9fafb; }

.footnote { font-size: 12px; color: #6b7280; margin-top: 18px; text-align: center; line-height: 1.6; }

/* Pay modal */
.pay-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.pay-modal { background: #fff; border-radius: 16px; max-width: 480px; width: 100%; padding: 28px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
.pay-title { font-size: 20px; color: #0045a1; margin: 0 0 6px; font-weight: 800; }
.pay-sub { font-size: 14px; color: #475569; margin: 0 0 18px; line-height: 1.5; }
.pay-label { display: block; font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0 6px; }
.pay-input { width: 100%; padding: 12px 14px; border: 2px solid #e5e7eb; border-radius: 10px; font-size: 16px; transition: border-color 0.15s; }
.pay-input:focus { outline: none; border-color: #2563eb; }
.pay-quickrow { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.chip { background: #f1f5f9; border: 1px solid #e2e8f0; color: #334155; padding: 6px 12px; border-radius: 999px; font-size: 12px; cursor: pointer; }
.chip:hover { background: #e2e8f0; }
.pay-error { background: #fee2e2; color: #991b1b; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-top: 14px; }
.pay-actions { display: flex; gap: 10px; margin-top: 22px; }
.pay-actions .btn-primary, .pay-actions .btn-secondary { padding: 12px 20px; min-width: 0; }

.pay-state { text-align: center; padding: 10px 0; }
.pay-state h3 { font-size: 20px; color: #0045a1; margin: 14px 0 8px; }
.pay-state p { font-size: 14px; color: #475569; line-height: 1.5; margin: 6px 0; }
.pay-state.success h3 { color: #059669; }
.pay-state.success .check { width: 64px; height: 64px; line-height: 64px; border-radius: 50%; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: #fff; font-size: 32px; font-weight: 800; margin: 0 auto 6px; }
.pay-state.error h3 { color: #b91c1c; }
.pay-state .muted { color: #6b7280; }
.pay-state .small { font-size: 12px; }
.pay-state code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; }

@media print {
  .page-wrapper { background: #fff; }
  header, footer, .cta-row, .pay-overlay { display: none !important; }
  .notice-card { box-shadow: none; padding: 0; border-top: 0; }
}
</style>
